import { useState, useMemo, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { Calendar, Users } from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from '../ui/command';
import {
  searchCaregivers,
  searchAssignments,
  parseDateQuery,
  searchCarePlans,
} from '../../lib/utils/search-helpers';
import type { Caregiver, CalendarEvent } from '../../lib/types/data.types';

interface CalendarSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caregivers: Caregiver[];
  events: CalendarEvent[];
  currentDate: Date;
  onCaregiverSelect: (caregiver: Caregiver) => void;
  onAssignmentSelect: (event: CalendarEvent) => void;
  onDateSelect: (date: Date) => void;
}

export function CalendarSearchDialog({
  open,
  onOpenChange,
  caregivers,
  events,
  currentDate,
  onCaregiverSelect,
  onAssignmentSelect,
  onDateSelect,
}: CalendarSearchDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Calculate current month range
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const currentMonthLabel = format(currentDate, 'MMMM yyyy');

  // Filter events to only include current month
  const currentMonthEvents = useMemo(() => {
    return events.filter(event =>
      isWithinInterval(event.startDate, { start: monthStart, end: monthEnd })
    );
  }, [events, monthStart, monthEnd]);

  // Search results - NO debouncing, search immediately
  const searchResults = useMemo(() => {
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery || trimmedQuery.length < 1) {
      return null;
    }

    // Get search results - use currentMonthEvents instead of all events
    return {
      caregivers: searchCaregivers(trimmedQuery, caregivers),
      assignments: searchAssignments(trimmedQuery, currentMonthEvents),
      dates: parseDateQuery(trimmedQuery),
      carePlans: searchCarePlans(trimmedQuery, currentMonthEvents),
    };
  }, [searchQuery, caregivers, currentMonthEvents]);

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  // Get caregiver name by ID
  const getCaregiverName = (caregiverId: string) => {
    const caregiver = caregivers.find(cg => cg.id === caregiverId);
    return caregiver?.fullName || 'Unknown';
  };

  // Get operational status label
  const getOperationalStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'on_assignment':
        return 'On Assignment';
      case 'on_leave':
        return 'On Leave';
      default:
        return status;
    }
  };

  // Handle care plan selection (navigate to first event)
  const handleCarePlanSelect = (carePlanId: string) => {
    const carePlan = searchResults?.carePlans.find(cp => cp.carePlanId === carePlanId);
    if (carePlan && carePlan.events.length > 0) {
      onAssignmentSelect(carePlan.events[0]);
    }
  };

  // Check if there are any results
  const hasResults = searchResults && (
    searchResults.caregivers.length > 0 ||
    searchResults.assignments.length > 0 ||
    searchResults.dates.length > 0 ||
    searchResults.carePlans.length > 0
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder="Search caregivers, patients, dates..."
        value={searchQuery}
        onValueChange={setSearchQuery}
        onClose={() => onOpenChange(false)}
      />
      <CommandList>
        {!searchQuery.trim() && (
          <div style={{ padding: '32px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#71717b', marginBottom: '16px' }}>
              Try searching for:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
              <kbd style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#f4f4f5', border: '1px solid #e4e4e7', borderRadius: '4px', color: '#71717b' }}>
                Caregiver names
              </kbd>
              <kbd style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#f4f4f5', border: '1px solid #e4e4e7', borderRadius: '4px', color: '#71717b' }}>
                Patient names
              </kbd>
              <kbd style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#f4f4f5', border: '1px solid #e4e4e7', borderRadius: '4px', color: '#71717b' }}>
                Care plan IDs
              </kbd>
              <kbd style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#f4f4f5', border: '1px solid #e4e4e7', borderRadius: '4px', color: '#71717b' }}>
                Dates
              </kbd>
            </div>
            <div style={{ fontSize: '11px', color: '#a1a1aa', marginTop: '16px' }}>
              Searching {currentMonthLabel} assignments
            </div>
          </div>
        )}

        {searchQuery.trim() && !hasResults && (
          <CommandEmpty>
            <div style={{ padding: '32px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#09090b', marginBottom: '4px' }}>
                No results found
              </div>
              <div style={{ fontSize: '12px', color: '#71717b' }}>
                Try a different search term
              </div>
            </div>
          </CommandEmpty>
        )}

        {/* Caregivers */}
        {searchResults && searchResults.caregivers.length > 0 && (
          <CommandGroup heading="Caregivers">
            {searchResults.caregivers.map((caregiver) => (
              <CommandItem
                key={caregiver.id}
                value={caregiver.fullName}
                onSelect={() => onCaregiverSelect(caregiver)}
                style={{ padding: '10px 8px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <img
                    src={caregiver.avatar}
                    alt={caregiver.fullName}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#09090b' }}>
                      {caregiver.fullName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#71717b' }}>
                      {caregiver.city} • {getOperationalStatusLabel(caregiver.operationalStatus)}
                    </div>
                  </div>
                  <CommandShortcut style={{ fontSize: '11px', color: '#71717b' }}>
                    Caregiver
                  </CommandShortcut>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Assignments */}
        {searchResults && searchResults.assignments.length > 0 && (
          <CommandGroup heading={`Assignments (${currentMonthLabel})`}>
            {searchResults.assignments.map((event) => (
              <CommandItem
                key={event.id}
                value={`${event.title}-${event.id}-${format(event.startDate, 'yyyy-MM-dd')}`}
                onSelect={() => onAssignmentSelect(event)}
                style={{ padding: '10px 8px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#f4f4f5',
                      flexShrink: 0,
                    }}
                  >
                    <Calendar size={16} style={{ color: '#71717b' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#09090b' }}>
                      {event.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#71717b' }}>
                      {getCaregiverName(event.caregiverId)} • {format(event.startDate, 'MMM d')}
                    </div>
                  </div>
                  {event.shift && (
                    <div
                      style={{
                        padding: '4px 8px',
                        fontSize: '11px',
                        backgroundColor: '#f4f4f5',
                        border: '1px solid #e4e4e7',
                        borderRadius: '4px',
                        color: '#71717b',
                      }}
                    >
                      {event.shift}
                    </div>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Dates */}
        {searchResults && searchResults.dates.length > 0 && (
          <CommandGroup heading="Dates">
            {searchResults.dates.map((date, index) => (
              <CommandItem
                key={`date-${index}`}
                value={format(date, 'MMMM d, yyyy')}
                onSelect={() => onDateSelect(date)}
                style={{ padding: '10px 8px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#f4f4f5',
                      flexShrink: 0,
                    }}
                  >
                    <Calendar size={16} style={{ color: '#71717b' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#09090b' }}>
                      Jump to {format(date, 'MMMM d, yyyy')}
                    </div>
                    <div style={{ fontSize: '12px', color: '#71717b' }}>
                      {format(date, 'EEEE')}
                    </div>
                  </div>
                  <CommandShortcut style={{ fontSize: '11px', color: '#71717b' }}>
                    Date
                  </CommandShortcut>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Care Plans */}
        {searchResults && searchResults.carePlans.length > 0 && (
          <CommandGroup heading="Care Plans">
            {searchResults.carePlans.map((carePlan) => (
              <CommandItem
                key={carePlan.carePlanId}
                value={carePlan.carePlanId}
                onSelect={() => handleCarePlanSelect(carePlan.carePlanId)}
                style={{ padding: '10px 8px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#f4f4f5',
                      flexShrink: 0,
                    }}
                  >
                    <Users size={16} style={{ color: '#71717b' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#09090b' }}>
                      Care Plan #{carePlan.carePlanId}
                    </div>
                    <div style={{ fontSize: '12px', color: '#71717b' }}>
                      {carePlan.events.length} assignment{carePlan.events.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <CommandShortcut style={{ fontSize: '11px', color: '#71717b' }}>
                    Care Plan
                  </CommandShortcut>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
